import { SingleValueCardColor, SingleValueCardType } from "constants/index";

export const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 3,
});

// A report figure that is not a usable number, resolved once for every caller.
// Report values reach these formatters as `number | string`: they are persisted
// to localStorage and hydrate untyped, the report layer writes "" for a figure
// the backend produced none of, and a hand-edited store can hold anything. Only
// a FINITE number is a figure -- NaN and Infinity render as "NaN%" and "∞%",
// which read as results rather than as the absence of one.
//
// NULLISH is accepted for the same reason, and it is the input this function
// used to throw on rather than answer about. `typeof null` is "object", so a
// null fell past the number branch and reached `value.trim()` -- an uncaught
// TypeError that blanked the whole Strategy report the moment Calculate wrote
// one into a summary card. A null is the plainest case of the question this
// function exists to answer: it is a figure that was not produced, so it gets
// the same `null` answer as "" rather than a crash. It arrives even though the
// payload types promise `number`, because a job result is cached on its DATA
// and never on the code that built it, so a result produced by an earlier
// backend outlives the frontend that could read its shape.
export const toFiniteNumber = (
  value: number | string | null | undefined,
): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  // `Number`, not `parseFloat`. parseFloat reads a numeric PREFIX and stops,
  // so it turns "4.5abc" into 4.5 and "12,345.67" into 12 -- a confident wrong
  // figure, which is the exact outcome N/A exists to avoid. `Number` rejects
  // trailing junk and thousands separators outright. The blank check comes
  // first because `Number("")` is 0, and "" is the sentinel for no figure at
  // all. It also makes this agree with `asCurrency` below, so one stored value
  // gets one reading rather than a different one per card type.
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

export const asCurrency = (value: number | string) =>
  currencyFormatter.format(Number(value));

// Never throws: `value.toFixed` was an uncaught TypeError for every value that
// was not a number, and one of those blanked the whole app on the Strategy
// report's Time-Weighted Return card. A numeric string is a recoverable figure
// and formats; anything that is not a figure returns a blank, leaving the
// DISPLAY decision to the card -- which renders N/A, because a figure the
// backend refused is a state and not a value.
// `digits` exists so the report charts can share this instead of keeping their
// own copies: the bars read to one decimal and everything else to two, and a
// private duplicate per precision is how two of them ended up with the same
// unguarded `value.toFixed` that crashed the page.
export const asPercentage = (value: number | string, digits = 2) => {
  const numeric = toFiniteNumber(value);
  return numeric === null ? "" : `${numeric.toFixed(digits)}%`;
};

// The CURRENCY and NUMBER paths keep the coercion they have always had:
// Intl.NumberFormat takes whatever arrives and renders "" as $0.00. That is
// wrong -- a figure that was never produced is not zero -- but it does not
// crash, and changing what ten shipped cards display is a separate decision
// from stopping the crash, so it is named as a follow-up rather than folded in
// here. `Number(...)` is what satisfies the signature under TS 4.9, whose
// `format` takes no string; it is output-identical to handing Intl the raw
// value for every input these reports produce, which formatters.test.ts pins.
// Both dispatchers below answer nullish with a blank BEFORE they read `type`,
// on the same terms as asPercentage above: the blank leaves the DISPLAY
// decision to the card, which renders N/A. Doing it here rather than only in
// the cards is what stops the crash coming back through a third caller -- the
// CURRENCY branch would send a null through `Number(null)` and print "$0.00",
// and the fallthrough `value.toString()` at the end is a second uncaught throw
// waiting on exactly the input that caused the first one.
export const formatValueWithSign = (
  value: number | string | null | undefined,
  type: SingleValueCardType,
) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (type === SingleValueCardType.CURRENCY) {
    return asCurrency(value);
  } else if (type === SingleValueCardType.PERCENTAGE) {
    const numeric = toFiniteNumber(value);
    if (numeric === null) {
      return "";
    }
    return numeric > 0 ? `+${asPercentage(numeric)}` : asPercentage(numeric);
  } else if (type === SingleValueCardType.NUMBER) {
    return new Intl.NumberFormat("en-US").format(Number(value));
  }
  return value.toString();
};

export const formatValue = (
  value: number | string | null | undefined,
  type: SingleValueCardType,
) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (type === SingleValueCardType.CURRENCY) {
    return asCurrency(value);
  } else if (type === SingleValueCardType.PERCENTAGE) {
    return asPercentage(value);
  } else if (type === SingleValueCardType.NUMBER) {
    return new Intl.NumberFormat("en-US").format(Number(value));
  }
  return value.toString();
};

// The stats endpoint reports money already scaled to millions, so the unit is
// part of the value's meaning and has to travel with it -- a bare "916.347" on
// a chart tooltip reads as dollars.
export const asMillions = (value: number) => `${asCurrency(value)}M`;

// The API sends ISO dates (2025-08-28); the app shows US ones (08/28/2025).
// Split the string rather than parse it: `new Date("2025-08-28")` is read as
// UTC midnight, which renders as the previous day anywhere west of Greenwich.
// Anything not in that shape is passed through rather than rearranged blindly.
export const asUsDate = (isoDate: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) {
    return isoDate;
  }
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};
